import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDashboardStore } from "../../stores/Layout/dashboard";
import "../../css/carousel.css";
import chevron from "../../assets/icons/chevron-left.svg";
import chevronRight from "../../assets/icons/chevron-right-dark.svg";
import { useNavigate } from "react-router-dom";
import { useTokenContext } from "../../context/tokenContext";

type DashboardCard = {
  type: "partner" | "customer";
  idRole?: number;
  idPartner?: number;
  idCustomer?: number;
  namePartner?: string;
  nameCustomer?: string;
  logo?: string;
};

const CarouselDashboard = () => {
  const { t } = useTranslation();
  const { cardsPartner, cardsCustomer } = useDashboardStore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const { setRoleId } = useTokenContext();

  const allCards = useMemo<DashboardCard[]>(
    () => [
      ...(cardsPartner ?? []).map((card) => ({
        ...card,
        type: "partner" as const,
      })),
      ...(cardsCustomer ?? []).map((card) => ({
        ...card,
        type: "customer" as const,
      })),
    ],
    [cardsPartner, cardsCustomer],
  );

  const handleCardClick = (card: DashboardCard) => {
    if (card.type === "partner" && card.idRole !== undefined) {
      setRoleId(card.idRole);
    }

    const navigateTo =
      (card.type === "partner" && card.idRole === 1) ||
      (card.type === "partner" && card.idRole === 2) ||
      (card.type === "partner" && card.idRole === 3)
        ? `/partner/${card.idPartner}/customers`
        : card.type === "customer"
        ? `/customer/${card.idCustomer}/missions`
        : `/error`;
    navigate(navigateTo);
  };

  const changeCard = useCallback((direction: -1 | 1) => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setCurrentIndex(
      (previousIndex) =>
        (previousIndex + direction + allCards.length) % allCards.length,
    );

    window.setTimeout(() => setIsTransitioning(false), 450);
  }, [allCards.length, isTransitioning]);

  const getCardPosition = (cardIndex: number) => {
    const totalCards = allCards.length;
    let relativeIndex = cardIndex - currentIndex;

    if (relativeIndex < 0) {
      relativeIndex += totalCards;
    }

    if (relativeIndex === 0) {
      return "is-active";
    }
    if (relativeIndex === 1) {
      return "is-next";
    }
    if (relativeIndex === totalCards - 1) {
      return "is-previous";
    }

    return "is-hidden";
  };

  const getCardTitle = (card: DashboardCard) =>
    card.namePartner || card.nameCustomer || t("dashboard.customer");

  if (allCards.length === 0) {
    return (
      <div className="carousel-empty" role="status">
        <span className="carousel-empty__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
              d="M3 21h18M5 21V7l7-4 7 4v14M9 10h.01M9 14h.01M15 10h.01M15 14h.01M10 21v-3h4v3"
            />
          </svg>
        </span>
        <p>{t("dashboard.companiesSubtitle")}</p>
      </div>
    );
  }

  return (
    <section
      className="carousel-container"
      aria-roledescription="carousel"
      aria-label={t("dashboard.companiesTitle")}
      onKeyDown={(event) => {
        if (allCards.length < 2) return;
        if (event.key === "ArrowLeft") changeCard(-1);
        if (event.key === "ArrowRight") changeCard(1);
      }}
    >
      {allCards.length > 1 && (
        <button
          type="button"
          className="carousel-control carousel-control--previous"
          onClick={() => changeCard(-1)}
          disabled={isTransitioning}
          aria-label={t("common.previous", { defaultValue: "Précédent" })}
        >
          <img src={chevron} alt="" aria-hidden="true" />
        </button>
      )}

      <div className="carousel-stage" aria-live="polite">
        {allCards.map((card, index) => {
          const position = getCardPosition(index);
          const isActive = position === "is-active";
          const cardTitle = getCardTitle(card);

          return (
            <button
              type="button"
              key={`${card.type}-${card.idPartner ?? card.idCustomer ?? index}`}
              className={`carousel-card ${position}`}
              onClick={() =>
                isActive ? handleCardClick(card) : setCurrentIndex(index)
              }
              tabIndex={isActive ? 0 : -1}
              aria-hidden={!isActive}
              aria-label={`${cardTitle} — ${
                card.type === "partner"
                  ? t("dashboard.partner")
                  : t("dashboard.customer")
              }`}
            >
              <span className="carousel-card__type">
                <span aria-hidden="true" />
                {card.type === "partner"
                  ? t("dashboard.partner")
                  : t("dashboard.customer")}
              </span>

              <div className="carousel-card__logo">
                {card.logo ? (
                  <img
                    src={`data:image/png;base64,${card.logo}`}
                    alt=""
                    loading={isActive ? "eager" : "lazy"}
                    decoding="async"
                  />
                ) : (
                  <span aria-hidden="true">{cardTitle.charAt(0).toUpperCase()}</span>
                )}
              </div>

              <div className="carousel-card__content">
                <h3>{cardTitle}</h3>
                {card.type === "customer" && card.namePartner && (
                  <p>{card.nameCustomer || t("dashboard.customer")}</p>
                )}
              </div>

              <span className="carousel-card__action" aria-hidden="true">
                {t("common.open", { defaultValue: "Accéder" })}
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.8"
                    d="m7 4 6 6-6 6"
                  />
                </svg>
              </span>
            </button>
          );
        })}
      </div>

      {allCards.length > 1 && (
        <>
          <button
            type="button"
            className="carousel-control carousel-control--next"
            onClick={() => changeCard(1)}
            disabled={isTransitioning}
            aria-label={t("common.next", { defaultValue: "Suivant" })}
          >
            <img src={chevronRight} alt="" aria-hidden="true" />
          </button>

          <div className="carousel-progress" aria-hidden="true">
            <span>
              {String(currentIndex + 1).padStart(2, "0")} /{" "}
              {String(allCards.length).padStart(2, "0")}
            </span>
            <div className="carousel-progress__track">
              <div
                className="carousel-progress__value"
                style={{
                  width: `${((currentIndex + 1) / allCards.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default CarouselDashboard;
